import { formatCO2Amount, formatFractionDisplay } from "utils";

import {
  ACCOUNTING_METHODOLOGY,
  ActivePracticeBundle,
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  CarbonProgramResult,
  CarbonRunnerErrorType,
  CropFCPO,
  FieldOutcomesByPractice,
  FieldPracticeResponse,
  PRACTICE_BUNDLE_SLUG,
  PRACTICE_SLUG,
  PayoutsByField,
  PracticeBundle,
  PracticeFCPO,
  PracticeSlug,
  SingleFieldOutcomes,
  ValidAggregation,
  ValidAggregationWithPayouts,
  ValidOrInvalidAggregation,
} from "models";

export const isSingleFieldOutcomes = (value?: CarbonProgramResult): value is SingleFieldOutcomes => !!value && "fieldOutcomes" in value;

export const isValidAggregation = (aggregation?: ValidOrInvalidAggregation): aggregation is ValidAggregation => !!aggregation?.validation?.valid;

export type FieldContributionStatus = {
  fieldsContributingToPayouts: number;
  totalFields: number;
};

export const isFieldEnrolled = (carbonProgramResult?: CarbonProgramResult): boolean =>
  // @ts-expect-error I don't want to deal with this every time
  !!carbonProgramResult?.fieldOutcomes?.crops?.isParticipating;

/**
 * Determines if a field can be deleted, if
 * - It's not enrolled in a non-inventory carbon program
 * - OR the carbon program is an inventory program (where fields can be deleted even if enrolled)
 */
export const canDeleteField = ({
  carbonProgramResult,
  carbonProgram,
}: {
  carbonProgramResult?: CarbonProgramResult;
  carbonProgram?: CarbonProgram | null;
}): boolean => {
  if (carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) {
    return true;
  }

  const enrolled = isFieldEnrolled(carbonProgramResult);
  return !enrolled;
};

export const hasActiveCropRotation = (
  fieldPractices?:
    | {
        activePracticeBundles?: ActivePracticeBundle[];
        practices?: PracticeFCPO[];
        crops?: CropFCPO[];
        fieldHasFailedVerification?: boolean;
      }
    | FieldPracticeResponse
    | undefined
): boolean => {
  if (!fieldPractices) return false;

  if ("activePracticeBundles" in fieldPractices) {
    return (
      (fieldPractices?.activePracticeBundles && !!fieldPractices?.activePracticeBundles.find((bundle) => bundle.slug === PRACTICE_BUNDLE_SLUG.CROPS)) || false
    );
  }

  if ("availablePracticeBundles" in fieldPractices) {
    return (
      (fieldPractices?.availablePracticeBundles &&
        !!fieldPractices?.availablePracticeBundles.find((bundle) => bundle.slug === PRACTICE_BUNDLE_SLUG.CROPS && bundle.active)) ||
      false
    );
  }

  return false;
};

export const fieldHasActivePractices = (
  fieldPractices?:
    | {
        availablePracticeBundles: ActivePracticeBundle[];
        practices: PracticeFCPO[];
        crops: CropFCPO[];
        fieldHasFailedVerification: boolean;
      }
    | FieldPracticeResponse
    | undefined
): boolean => !!fieldPractices?.availablePracticeBundles.some((bundle) => bundle.active);

export const areFieldOutcomesValidForYear = (fieldOutcomes: FieldOutcomesByPractice, year: number): boolean =>
  Object.values(fieldOutcomes || {})
    .filter(({ isParticipating }) => isParticipating)
    .every((outcome) =>
      Object.entries(outcome.years).every(
        ([yearKey, yearlyData]) => yearlyData.validation?.valid || (!yearlyData.isBaselineYear && yearKey !== year.toString())
      )
    );

export const isNextYearOutcomeValid = (fieldOutcomes: FieldOutcomesByPractice, reportingYear: number, carbonProgram: CarbonProgram | null): boolean => {
  const nextYear = reportingYear + 1;

  return Object.entries(fieldOutcomes || {})
    .filter(([slug]) => (carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.intervention ? slug !== PRACTICE_SLUG.CROPS : true))
    .every(([_slug, practice]) => (practice.isParticipating ? practice.years?.[nextYear]?.validation?.valid : true));
};

const hasValidPayouts = (aggregation: ValidOrInvalidAggregation): aggregation is ValidAggregationWithPayouts =>
  aggregation.validation.valid && "payouts" in aggregation;

export const calculateAvgPayoutForYear = (fieldOutcomes: FieldOutcomesByPractice, year: number): number =>
  Object.values(fieldOutcomes || {}).reduce((acc, outcome) => {
    const yearlyData = outcome.years[year];
    if (yearlyData && hasValidPayouts(yearlyData)) {
      return acc + yearlyData.payouts.avg;
    }
    return acc;
  }, 0);

const checkPracticeDependency = (calculation: SingleFieldOutcomes, practiceSlug: PracticeSlug, year: number) =>
  calculation?.fieldOutcomes?.[practiceSlug]?.years?.[year]?.validation?.valid ?? false;

export const isPracticeDependencyCompleted = (calculation: SingleFieldOutcomes, practiceSlug: PracticeSlug, year: number): boolean => {
  switch (practiceSlug) {
    case PRACTICE_SLUG.CROPS:
      return true;
    case PRACTICE_SLUG.MINERAL_FERTILIZERS:
      return checkPracticeDependency(calculation, PRACTICE_SLUG.ORGANIC_FERTILIZERS, year);
    case PRACTICE_SLUG.ORGANIC_FERTILIZERS:
      return checkPracticeDependency(calculation, PRACTICE_SLUG.MINERAL_FERTILIZERS, year);
    case PRACTICE_SLUG.TILLAGES:
      return checkPracticeDependency(calculation, PRACTICE_SLUG.SEEDINGS, year);
    case PRACTICE_SLUG.SEEDINGS:
      return checkPracticeDependency(calculation, PRACTICE_SLUG.TILLAGES, year);
    default:
      return false;
  }
};

export const isPracticeEnabledByBundle = (practiceSlug: PracticeSlug, practiceBundles?: PracticeBundle[]): boolean => {
  if (!practiceBundles) return false;

  const isBundleActive = (bundleSlug: string, bundles: PracticeBundle[]): boolean => bundles.find((bundle) => bundle?.slug === bundleSlug)?.active ?? false;

  if (practiceSlug === PRACTICE_SLUG.TILLAGES || practiceSlug === PRACTICE_SLUG.SEEDINGS) return isBundleActive("tillage", practiceBundles);
  if (practiceSlug === PRACTICE_SLUG.ORGANIC_FERTILIZERS || practiceSlug === PRACTICE_SLUG.MINERAL_FERTILIZERS)
    return isBundleActive("fertilizer", practiceBundles);

  return true;
};

const calculateFieldPayoutsForYear = (outcomesData: CarbonProgramOutcomesResponse, year: number): PayoutsByField => {
  const fieldPayouts: PayoutsByField = {};

  // Process each field
  Object.entries(outcomesData).forEach(([fieldId, calculation]) => {
    const practicePayouts: Record<PracticeSlug, number> = {
      [PRACTICE_SLUG.CROPS]: 0,
      [PRACTICE_SLUG.MINERAL_FERTILIZERS]: 0,
      [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: 0,
      [PRACTICE_SLUG.PLANT_PROTECTIONS]: 0,
      [PRACTICE_SLUG.SEEDINGS]: 0,
      [PRACTICE_SLUG.TILLAGES]: 0,
      [PRACTICE_SLUG.LAND_USE_CHANGES]: 0,
    };

    if ("fieldOutcomes" in calculation) {
      // Check each practice
      Object.entries(calculation.fieldOutcomes).forEach(([practiceSlug, practice]) => {
        // Only process if it's a valid practice
        if (Object.values(PRACTICE_SLUG).includes(practiceSlug as PracticeSlug)) {
          const typedPracticeSlug = practiceSlug as PracticeSlug;

          // Check if practice is participating
          if (!practice.isParticipating) return;

          const yearData = practice.years[year];
          if (yearData?.validation?.valid && !yearData.isBaselineYear) {
            const validYearData = yearData as ValidAggregation;
            if (validYearData.payouts?.avg) {
              practicePayouts[typedPracticeSlug] = validYearData.payouts.avg;
            }
          }
        }
      });
    }

    fieldPayouts[fieldId] = {
      totalPayout: Object.values(practicePayouts).reduce((sum, payout) => sum + payout, 0),
      practicePayouts,
    };
  });

  return fieldPayouts;
};

export const calculateTotalProgramPayoutsForYear = (outcomesData: CarbonProgramOutcomesResponse, year: number) => {
  const fieldPayouts = calculateFieldPayoutsForYear(outcomesData, year);

  const summary = {
    totalProgramPayout: 0,
    totalByPractice: {
      [PRACTICE_SLUG.CROPS]: 0,
      [PRACTICE_SLUG.MINERAL_FERTILIZERS]: 0,
      [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: 0,
      [PRACTICE_SLUG.PLANT_PROTECTIONS]: 0,
      [PRACTICE_SLUG.SEEDINGS]: 0,
      [PRACTICE_SLUG.TILLAGES]: 0,
      [PRACTICE_SLUG.LAND_USE_CHANGES]: 0,
    },
    participatingFieldCount: 0,
    activeFields: [] as string[],
  };

  Object.entries(fieldPayouts).forEach(([fieldId, field]) => {
    // Check if there are any non-zero payouts
    const hasNonZeroPayout = Object.values(field.practicePayouts).some((payout) => payout !== 0);

    if (hasNonZeroPayout) {
      summary.participatingFieldCount += 1;
      summary.activeFields.push(fieldId);
      summary.totalProgramPayout += field.totalPayout;

      Object.entries(field.practicePayouts).forEach(([practice, payout]) => {
        summary.totalByPractice[practice as PracticeSlug] += payout;
      });
    }
  });

  return summary;
};

export const footprintingPayoutForYear = (cp: CarbonProgram, year: number) => {
  if (!cp.farmLevelFootprinting) {
    return 0;
  }

  const bonuses = cp.farmLevelFootprinting.formSubmissionBonuses;
  let payout = 0;
  if (bonuses.farm) {
    payout += bonuses.farm[year];
  }

  const cropFactor = bonuses.cropPayouts === "per-crop" ? cp.farmLevelFootprinting?.programCrops.length ?? 0 : 1;
  if (bonuses.crop) {
    payout += bonuses.crop[year] * cropFactor;
  }

  return payout;
};

export const calculateRemovalsProgress = (program: CarbonProgram | null, programOutcomes: CarbonProgramOutcomesResponse | undefined, currentYear: number) => {
  if (!programOutcomes) return 0;

  let validPracticesCount = 0;
  let totalRequiredPractices = 0;

  Object.values(programOutcomes).forEach((outcome) => {
    if ("fieldOutcomes" in outcome) {
      Object.values(outcome.fieldOutcomes).forEach((practice) => {
        if (Object.values(practice.required).some((requirement) => requirement)) {
          totalRequiredPractices += 1;

          const practiceIsValid =
            program?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory
              ? practice.years[currentYear]?.validation?.valid
              : Object.values(practice.years).every((aggregation) => aggregation.validation?.valid);

          if (practice.isParticipating && practiceIsValid) {
            validPracticesCount += 1;
          }
        }
      });
    }
  });

  return totalRequiredPractices > 0 ? formatFractionDisplay(validPracticesCount / totalRequiredPractices) : 0;
};

const fieldHasValidPayouts = (fieldOutcome: SingleFieldOutcomes, reportingYear: number): boolean => {
  if (!fieldOutcome?.fieldOutcomes) return false;

  return Object.values(fieldOutcome.fieldOutcomes).some((practiceOutcome) => {
    if (!practiceOutcome?.years) return false;

    const yearData = practiceOutcome.years[reportingYear];
    return yearData && !yearData.isBaselineYear && yearData.validation?.valid && "payouts" in yearData && Number(yearData.payouts?.avg);
  });
};

export const calculateFieldContributionStatus = (carbonProgramOutcomes: CarbonProgramOutcomesResponse, reportingYear: number): FieldContributionStatus => {
  // doing this to get all the fields, not just the single field outcomes without errors
  const singleFieldOutcomes = Object.values(carbonProgramOutcomes ?? {});

  const totalFields = singleFieldOutcomes.filter((outcome) => isSingleFieldOutcomes(outcome) && outcome.fieldOutcomes.crops?.isParticipating).length;
  let fieldsContributingToPayouts = 0;

  singleFieldOutcomes.filter(isSingleFieldOutcomes).forEach((fieldOutcome) => {
    const isParticipating = fieldOutcome?.fieldOutcomes?.crops?.isParticipating;

    const hasValidPayouts = fieldHasValidPayouts(fieldOutcome, reportingYear);

    // Only count fields that meet both criteria (valid payout data and participating)
    if (isParticipating && hasValidPayouts) {
      fieldsContributingToPayouts += 1;
    }
  });

  return {
    fieldsContributingToPayouts,
    totalFields,
  };
};
export const someOutcomeHasErrorOfType = (outcomesData: CarbonProgramOutcomesResponse, errorType: CarbonRunnerErrorType) =>
  Object.values(outcomesData).some((result) => "error" in result && result.error.message.includes(errorType));

/**
 * Gets the internal name of a carbon program if available, otherwise falls back to the program name.
 * This utility is specifically for admin views to display program names that farmers shouldn't see.
 */
export const getProgramInternalName = (program: CarbonProgram): string =>
  (program as CarbonProgram & { internalName?: string }).internalName || program.programName;

export const calculateTotalCO2ForYear = (outcomesData: CarbonProgramOutcomesResponse, year: number) => {
  // total CO2 sequestered from all fields (‼️ not sure if this covers what we need)
  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(outcomesData).filter(isSingleFieldOutcomes);
  const totalCO2 = singleFieldOutcomes.reduce<{ max: number; min: number; avg: number }>(
    (sum, outcome) => {
      const yearData = outcome?.fieldOutcomes?.crops?.years?.[year];
      if (!yearData || !("points" in yearData)) {
        return sum;
      }
      const { points } = yearData;
      if (!points) return sum;
      const fieldTotalCO2Max = points.removals.max + points.avoidances.max + points.reductions.max;
      const fieldTotalCO2Min = points.removals.min + points.avoidances.min + points.reductions.min;
      const fieldTotalCO2Avg = points.removals.avg + points.avoidances.avg + points.reductions.avg;

      return { max: sum.max + fieldTotalCO2Max, min: sum.min + fieldTotalCO2Min, avg: sum.avg + fieldTotalCO2Avg };
    },
    { max: 0, min: 0, avg: 0 }
  );
  return {
    max: parseFloat(formatCO2Amount(Math.abs(totalCO2.max))),
    min: parseFloat(formatCO2Amount(Math.abs(totalCO2.min))),
    avg: parseFloat(formatCO2Amount(Math.abs(totalCO2.avg))),
  };
};
